import { DURATION, notify, TYPE } from "@8x8/oxygen-toaster";

import { ERRORS } from "@/react/components/table/useTableMutations";

import mapTapValidationErrors from "./mapTapValidationErrors";

/**
 * @typedef {Object} TAPResponseEntity
 * @property {string} identifier
 * @property {OperationResult} operationResult
 */

/**
 * @typedef {Object} OperationResult
 * @property {Object<string, *>?} success
 * @property {TAPErrorResponse?} failure
 */

/**
 * @typedef {Object} TAPErrorResponse
 * @property {string} message
 * @property {Array<{field: string, message: string}>} errors
 */

/**
 *
 * Update the table state based on bulk responses of type [TAPResponseEntity]{@link https://github.com/8x8/tap_tap-tools_tool8r-backend/blob/master/service/src/main/java/com/_8x8/tap_backend_r/exception/TAPResponseEntity.java}
 *
 *
 * @param {Map<string, *>}rows
 * @param {function(Map<string, *>): void} setRows
 * @param {function(function(Array<*>): Array<*>): void} setData
 * @param {string} responseMappingKey
 * @param {string} idKey
 * @param {string} errorKey
 * @param {function(number, number): string} notifyContent
 * @param {boolean} isVoidResponse Some successful mutations return void, so we need to handle them differently.
 * @param {string | null} customFallbackKey A row attribute that will be used to show row-level errors.
 * @returns {function(Array<TAPResponseEntity>): void}
 */
export const handleMutationResponse =
  ({
    rows,
    setRows,
    setData,
    responseMappingKey,
    idKey,
    notifyContent,
    isVoidResponse,
    customFallbackKey = null,
  }) =>
  (response) =>
    setData((oldData) => {
      const dataToUpdate = new Map();
      const rowsToUpdate = new Map(rows);

      const mutationCount = rowsToUpdate.size;

      rowsToUpdate.forEach(
        handleRowResponse(
          response,
          responseMappingKey,
          idKey,
          isVoidResponse,
          rowsToUpdate,
          dataToUpdate,
          customFallbackKey
        )
      );

      notifyMutations(mutationCount, rowsToUpdate, notifyContent);

      setRows(rowsToUpdate);

      return updateData(oldData, dataToUpdate, idKey);
    });

/**
 * Find the response entity that matches the draft data by identifier or responseMappingKey
 *
 * @param {Object} draftData
 * @param {string} responseMappingKey
 * @returns {function(TAPResponseEntity): boolean}
 */
function matchByIdentifierOrField(draftData, responseMappingKey) {
  return ({ identifier, operationResult }) => {
    const value = draftData[responseMappingKey];
    // On some tables, newly created rows cannot be identified by the ID, so we
    // need to use the responseMappingKey, which can be the name or some other
    // field.
    return (
      identifier == value ||
      (operationResult?.success &&
        operationResult?.success[responseMappingKey] == value)
    );
  };
}

/**
 * Parse error data from the TAP backend into the _errors object that
 * is attached to a table row.
 *
 * @param errors
 * @param message
 * @param fallbackKey fallback key for all non-specific errors
 * @return {Object<string, Array<string>>|{}}
 */
const parseErrors = (errors, message, fallbackKey) => {
  if (!errors?.length) {
    return { [fallbackKey]: [message] };
  }

  return mapTapValidationErrors(errors, fallbackKey);
};

/**
 *
 * @param {Array<TAPResponseEntity>} response
 * @param {string} responseMappingKey
 * @param {string} idKey
 * @param {boolean} isVoidResponse
 * @param {Map<string, *>} newRows
 * @param {Map<string, *>} dataToUpdate
 * @param customFallbackKey custom key to fall back on when errors do not contain key data
 * @returns {function(Object, string): void}
 */
const handleRowResponse = (
  response,
  responseMappingKey,
  idKey,
  isVoidResponse,
  newRows,
  dataToUpdate,
  customFallbackKey = null
) => {
  const fallbackKey = customFallbackKey ?? responseMappingKey ?? idKey;
  return (row, key) => {
    const responseEntity =
      response?.find(matchByIdentifierOrField(row, responseMappingKey)) ||
      response?.find(matchByIdentifierOrField(row, idKey));

    if (!responseEntity) {
      // Treat void responses as successful
      if (isVoidResponse) {
        newRows.delete(key);
      }
      return;
    }

    if (responseEntity.operationResult.success) {
      dataToUpdate.set(key, {
        ...row,
        ...responseEntity.operationResult.success,
      });
      newRows.delete(key);
    }

    if (responseEntity.operationResult.failure) {
      const { errors, message } = responseEntity.operationResult.failure;
      dataToUpdate.set(key, {
        ...row,
        [ERRORS]: parseErrors(errors, message, fallbackKey),
      });
    }
  };
};

/**
 * @param {number} mutationCount
 * @param {Map<string, *>} rowsToUpdate
 * @param {function(number, number): string} notifyContent
 */
function notifyMutations(mutationCount, rowsToUpdate, notifyContent) {
  const successfulMutationCount = mutationCount - rowsToUpdate.size;
  const isCompleteFailure = successfulMutationCount === 0;
  const isCompleteSuccess = successfulMutationCount === mutationCount;
  const successType = isCompleteSuccess ? TYPE.success : TYPE.info;
  notify({
    type: isCompleteFailure ? TYPE.error : successType,
    content: notifyContent(successfulMutationCount, mutationCount),
    duration:
      successfulMutationCount === mutationCount ? 5000 : DURATION.INFINITE,
  });
}

/**
 * @param {Array<*>} oldData
 * @param {Map<string, *>} dataToUpdate
 * @param {string} idKey
 * @returns {Array<*>}
 */
function updateData(oldData, dataToUpdate, idKey) {
  return oldData.map((data) => {
    if (dataToUpdate.has(data[idKey])) {
      return {
        ...data,
        ...dataToUpdate.get(data[idKey]),
      };
    }
    return data;
  });
}
